import { useTranslation } from 'react-i18next';

export default function SystemArchitecture() {
  const { t } = useTranslation();
  const layers = [
    { title: 'Data Intelligence Layer', subtitle: t('coreLogic.step0_sub'), modules: [{ name: 'Global News API', desc: t('coreLogic.step0_i0') }, { name: 'Social Sentiment', desc: t('coreLogic.step0_i2') }, { name: 'Macro Economic', desc: t('coreLogic.step0_i1') }, { name: 'Market Data', desc: t('coreLogic.step0_i3') }, { name: 'On-chain Data', desc: 'Blockchain Data' }] },
    { title: 'PolyAi-Claw Intelligence Engine', subtitle: t('hero.badge'), highlight: true, modules: [{ name: 'Event Detection AI', desc: 'Key event signal recognition' }, { name: 'Bayesian Probability', desc: t('coreLogic.step1_sub') }, { name: 'Sentiment Correction', desc: 'Market sentiment bias correction' }, { name: 'Market Pricing Analyzer', desc: t('coreLogic.step2_i0') }, { name: 'Deviation Scanner', desc: t('coreLogic.step2_i2') }] },
    { title: 'Decision Engine', subtitle: t('coreLogic.step2_sub'), modules: [{ name: 'Expected Value Engine', desc: 'EV advantage calculation' }, { name: 'Strategy Generator', desc: 'Generate trading strategy' }, { name: 'Signal Trigger System', desc: 'Trigger trading signal' }] },
    { title: 'Risk Control System', subtitle: t('fundManagement.riskTitle'), modules: [{ name: 'Exposure Control', desc: t('fundManagement.stat_maxSingle') }, { name: 'Correlation Hedging', desc: 'Event correlation hedging' }, { name: 'Capital Allocation', desc: t('fundManagement.stat_diversify') }] },
    { title: 'Execution Layer', subtitle: t('coreLogic.step3_sub'), modules: [{ name: 'Smart Order Router', desc: 'Intelligent order routing' }, { name: 'Polygon Transaction', desc: t('coreLogic.step3_i2') }, { name: 'Smart Contract', desc: t('coreLogic.step3_i3') }] },
    { title: 'Prediction Market', subtitle: 'Prediction Markets', modules: [{ name: 'Polymarket', desc: 'Primary trading market' }, { name: 'Kalshi', desc: 'Compliant prediction market' }] },
    { title: 'Settlement Layer', subtitle: t('coreLogic.step4_sub'), modules: [{ name: 'USDC Settlement', desc: t('coreLogic.step3_i2') }, { name: 'Profit Distribution', desc: t('coreLogic.step4_i1') }, { name: 'Auto Compounding', desc: t('coreLogic.step4_i2') }] }
  ];

  return (
    <section id="system-architecture" className="py-24 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #0c0814 0%, #100b1e 100%)' }}>
      <div className="absolute inset-0 grid-bg pointer-events-none opacity-50" />
      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        <div className="text-center mb-10 md:mb-16">
          <div className="badge-purple mb-6">{t('sysArch.badge')}</div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 font-heading">
            {t('sysArch.title1')}
            <span className="text-gradient-aurora"> {t('sysArch.title2')}</span>
          </h2>
          <p className="text-lg text-white/45 max-w-3xl mx-auto">{t('sysArch.desc')}</p>
        </div>
        <div className="relative space-y-4">
          {layers.map((layer, index) => (
            <div key={index} className="relative">
              <div className={`rounded-xl p-5 md:p-6 border transition-all ${layer.highlight ? 'border-purple-400/30' : 'bg-white/[0.025] border-purple-500/8 hover:border-purple-400/18'}`}
                style={layer.highlight ? { background: 'linear-gradient(135deg, rgba(147,51,234,0.1), rgba(124,58,237,0.06))' } : {}}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className={`text-lg font-bold mb-1 font-heading ${layer.highlight ? 'text-purple-300' : 'text-white'}`}>{layer.title}</h3>
                    <p className={`text-sm ${layer.highlight ? 'text-purple-400/60' : 'text-white/35'}`}>{layer.subtitle}</p>
                  </div>
                  {layer.highlight && (
                    <div className="px-3 py-1 rounded-full text-xs font-medium text-purple-300 border border-purple-400/25 whitespace-nowrap" style={{ background: 'rgba(147,51,234,0.12)' }}>
                      {t('sysArch.coreBadge')}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2.5">
                  {layer.modules.map((module, idx) => (
                    <div key={idx} className={`rounded-lg p-3 border transition-all group ${layer.highlight ? 'border-purple-400/15 hover:bg-purple-400/12' : 'bg-white/[0.025] border-white/5 hover:bg-white/[0.05] hover:border-purple-400/15'}`}
                      style={layer.highlight ? { background: 'rgba(147,51,234,0.07)' } : {}}>
                      <div className="flex items-start gap-2">
                        <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${layer.highlight ? 'bg-purple-400/20' : 'bg-white/5'}`}>
                          <i className={`ri-checkbox-circle-fill text-xs ${layer.highlight ? 'text-purple-400' : 'text-purple-400/50'}`}></i>
                        </div>
                        <div className="min-w-0">
                          <h4 className={`font-semibold text-xs mb-0.5 truncate ${layer.highlight ? 'text-white' : 'text-white/70'}`}>{module.name}</h4>
                          <p className={`text-xs truncate ${layer.highlight ? 'text-purple-400/50' : 'text-white/30'}`}>{module.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {index < layers.length - 1 && (
                <div className="flex justify-center my-2">
                  <div className="w-8 h-8 rounded-full bg-purple-400/10 border border-purple-400/20 flex items-center justify-center">
                    <i className="ri-arrow-down-line text-sm text-purple-400"></i>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { icon: 'ri-cpu-line', titleKey: 'sysArch.feat0_title', descKey: 'sysArch.feat0_desc' },
            { icon: 'ri-shield-check-line', titleKey: 'sysArch.feat1_title', descKey: 'sysArch.feat1_desc' },
            { icon: 'ri-git-branch-line', titleKey: 'sysArch.feat2_title', descKey: 'sysArch.feat2_desc' },
          ].map(item => (
            <div key={item.titleKey} className="premium-card premium-card-hover rounded-xl p-6">
              <div className="w-12 h-12 rounded-xl bg-purple-400/10 border border-purple-400/20 flex items-center justify-center mb-4">
                <i className={`${item.icon} text-2xl text-purple-400`}></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2 font-heading">{t(item.titleKey)}</h4>
              <p className="text-white/45 text-sm leading-relaxed">{t(item.descKey)}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 md:mt-14 bg-white/[0.02] border border-purple-500/8 rounded-xl p-5 md:p-8">
          <h3 className="text-xl md:text-2xl font-bold text-white mb-6 text-center font-heading">{t('sysArch.dataflow_title')}</h3>
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 text-sm">
            {['DATA LAYER', 'AI INTELLIGENCE', 'DECISION ENGINE', 'RISK CONTROL', 'EXECUTION', 'PREDICTION MARKET', 'SETTLEMENT'].map((label, i, arr) => (
              <>
                <span key={label} className="px-3 py-1.5 border border-purple-400/22 text-purple-300 rounded-full font-medium whitespace-nowrap text-xs" style={{ background: 'rgba(147,51,234,0.08)' }}>{label}</span>
                {i < arr.length - 1 && <i key={`arr-${i}`} className="ri-arrow-right-line text-purple-400/30 text-base"></i>}
              </>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
